import { useEffect, useState } from "react";
import { useAutosaveOnboarding, useOnboardingProgress } from "@/lib/onboarding-progress";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import {
  Building2, MapPin, Landmark, Sparkles, CheckCircle2, ChevronRight, ChevronLeft,
  GraduationCap, ShieldCheck, Rocket, Wallet, Bus,
} from "lucide-react";
import { track } from "@/lib/analytics";

type Step = {
  key: string;
  title: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
};

const STEPS: Step[] = [
  { key: "identity",   title: "School identity",     hint: "The basics — how families will recognise you.", icon: Building2 },
  { key: "location",   title: "Location & contact",  hint: "Where you are and how to reach the front office.", icon: MapPin },
  { key: "compliance", title: "Compliance",          hint: "Registration & tax details for receipts.", icon: ShieldCheck },
  { key: "banking",    title: "Settlement banking",  hint: "Where TriSphere Pay will settle your money.", icon: Landmark },
  { key: "modules",    title: "Modules",             hint: "Pick what to switch on first.", icon: Rocket },
  { key: "seed",       title: "Launch",              hint: "Optionally seed a live demo to explore.", icon: Sparkles },
];

type Props = {
  userId: string;
  onLinked: (schoolId: string) => void;
  onCancel?: () => void;
};

export function SchoolOnboardingWizard({ userId, onLinked, onCancel }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "",
    school_type: "day",
    subscription_plan: "growth",
    district: "",
    region: "Central",
    phone: "",
    email: "",
    latitude: 0.3476,
    longitude: 32.5825,
    registration_no: "",
    ura_tin: "",
    settlement_bank: "",
    settlement_account: "",
    student_count: 0,
    staff_count: 0,
    modules: {
      pay: true,
      transport: true,
      canteen: true,
      health: false,
      bookshop: true,
      academics: false,
    },
    seedDemo: true,
  });

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Resume from saved onboarding
  const { data: saved } = useOnboardingProgress("school", null);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated) return;
    if (saved && !saved.completed) {
      const st = saved.state as Partial<typeof form>;
      setForm((f) => ({ ...f, ...st }));
      setStep(saved.current_step ?? 0);
    }
    setHydrated(true);
  }, [saved, hydrated]);
  useAutosaveOnboarding("school", step, STEPS.length, form as unknown as Record<string, unknown>, { enabled: hydrated });


  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.from("schools").insert({
        name: form.name.trim(),
        school_type: form.school_type as "boarding" | "day" | "international" | "mixed" | "university",
        subscription_plan: form.subscription_plan as "starter" | "growth" | "enterprise",
        district: form.district || "Kampala",
        region: form.region,
        phone: form.phone,
        email: form.email,
        latitude: form.latitude,
        longitude: form.longitude,
        registration_no: form.registration_no,
        ura_tin: form.ura_tin,
        settlement_bank: form.settlement_bank,
        settlement_account: form.settlement_account,
        student_count: form.student_count,
        staff_count: form.staff_count,
        active: true,
      } as never).select("id").single();
      if (error) throw error;

      const { error: linkErr } = await supabase.from("profiles").update({ school_id: data.id }).eq("id", userId);
      if (linkErr) throw linkErr;

      if (form.seedDemo) {
        // Seed live demo data via SECURITY DEFINER RPC
        const { error: seedErr } = await (supabase.rpc as any)("seed_demo_for_school", { _school_id: data.id });
        if (seedErr) console.warn("[seed]", seedErr.message);
      }
      return data.id as string;
    },
    onSuccess: (schoolId) => {
      track("school_onboarded", { school_id: schoolId, seeded: form.seedDemo });
      toast.success("Your school is live 🎉");
      qc.invalidateQueries();
      onLinked(schoolId);
    },
    onError: (e: Error) => toast.error(e.message ?? "Could not create school"),
  });

  const canAdvance = (() => {
    switch (STEPS[step].key) {
      case "identity":   return form.name.trim().length >= 3;
      case "location":   return form.district.trim() && form.phone.trim();
      case "compliance": return true;
      case "banking":    return true;
      case "modules":    return true;
      default: return true;
    }
  })();

  const progress = ((step + 1) / STEPS.length) * 100;
  const S = STEPS[step];

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      {/* Rail */}
      <Card className="p-4 bg-gradient-to-b from-primary/10 via-card to-card">
        <Badge variant="secondary" className="gap-1"><GraduationCap className="h-3 w-3" />School onboarding</Badge>
        <h2 className="mt-2 font-display text-xl">Let's get {form.name || "your school"} live</h2>
        <p className="mt-1 text-xs text-muted-foreground">6 quick steps · ≈ 3 minutes</p>
        <Progress value={progress} className="mt-3 h-1.5" />
        <ol className="mt-4 space-y-1">
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            const Icon = s.icon;
            return (
              <li key={s.key}>
                <button
                  disabled={i > step}
                  onClick={() => setStep(i)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition ${
                    active ? "bg-primary/10 text-primary" : done ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  <span className={`grid h-6 w-6 place-items-center rounded-full ${done ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="font-medium">{s.title}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </Card>

      {/* Panel */}
      <Card className="flex min-h-[520px] flex-col p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <S.icon className="h-3.5 w-3.5" /> Step {step + 1} of {STEPS.length}
            </div>
            <h3 className="mt-1 font-display text-2xl">{S.title}</h3>
            <p className="text-sm text-muted-foreground">{S.hint}</p>
          </div>
          {onCancel && <Button variant="ghost" size="sm" onClick={onCancel}>I have a school already</Button>}
        </div>

        <div className="mt-6 flex-1">
          {S.key === "identity" && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="School name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Kampala Excellence Academy" /></Field>
              <Field label="School type">
                <RadioGroup value={form.school_type} onValueChange={(v) => set("school_type", v)} className="grid grid-cols-3 gap-2">
                  {["day","boarding","mixed"].map(t => (
                    <label key={t} className={`cursor-pointer rounded-lg border p-2 text-center text-sm capitalize ${form.school_type===t?"border-primary bg-primary/5":""}`}>
                      <RadioGroupItem value={t} className="sr-only" />{t}
                    </label>
                  ))}
                </RadioGroup>
              </Field>
              <Field label="Subscription plan">
                <RadioGroup value={form.subscription_plan} onValueChange={(v) => set("subscription_plan", v)} className="grid grid-cols-3 gap-2">
                  {["starter","growth","enterprise"].map(t => (
                    <label key={t} className={`cursor-pointer rounded-lg border p-2 text-center text-sm capitalize ${form.subscription_plan===t?"border-primary bg-primary/5":""}`}>
                      <RadioGroupItem value={t} className="sr-only" />{t}
                    </label>
                  ))}
                </RadioGroup>
              </Field>
              <Field label="Rough number of learners"><Input type="number" value={form.student_count || ""} onChange={(e) => set("student_count", parseInt(e.target.value || "0"))} placeholder="e.g. 450" /></Field>
            </div>
          )}

          {S.key === "location" && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="District *"><Input value={form.district} onChange={(e) => set("district", e.target.value)} placeholder="Kampala" /></Field>
              <Field label="Region">
                <RadioGroup value={form.region} onValueChange={(v) => set("region", v)} className="grid grid-cols-4 gap-2">
                  {["Central","Eastern","Western","Northern"].map(r => (
                    <label key={r} className={`cursor-pointer rounded-lg border p-2 text-center text-xs ${form.region===r?"border-primary bg-primary/5":""}`}>
                      <RadioGroupItem value={r} className="sr-only" />{r}
                    </label>
                  ))}
                </RadioGroup>
              </Field>
              <Field label="Front office phone *"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+256 700 000 000" /></Field>
              <Field label="Contact email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="admin@school.ac.ug" /></Field>
              <Field label="Latitude"><Input type="number" step="0.0001" value={form.latitude} onChange={(e) => set("latitude", parseFloat(e.target.value))} /></Field>
              <Field label="Longitude"><Input type="number" step="0.0001" value={form.longitude} onChange={(e) => set("longitude", parseFloat(e.target.value))} /></Field>
            </div>
          )}

          {S.key === "compliance" && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Ministry registration no."><Input value={form.registration_no} onChange={(e) => set("registration_no", e.target.value)} placeholder="MoES/PS/00123" /></Field>
              <Field label="URA TIN"><Input value={form.ura_tin} onChange={(e) => set("ura_tin", e.target.value)} placeholder="1000-000-000" /></Field>
              <Field label="Number of staff"><Input type="number" value={form.staff_count || ""} onChange={(e) => set("staff_count", parseInt(e.target.value || "0"))} /></Field>
              <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
                <ShieldCheck className="mb-1 h-4 w-4 text-primary" />
                Compliance details print on receipts and settle reports. You can update these anytime from Settings.
              </div>
            </div>
          )}

          {S.key === "banking" && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Settlement bank"><Input value={form.settlement_bank} onChange={(e) => set("settlement_bank", e.target.value)} placeholder="Stanbic Bank Uganda" /></Field>
              <Field label="Settlement account"><Input value={form.settlement_account} onChange={(e) => set("settlement_account", e.target.value)} placeholder="9030-0000-0000" /></Field>
              <div className="col-span-full rounded-lg border bg-emerald-50/40 p-3 text-xs">
                <Wallet className="mb-1 h-4 w-4 text-emerald-700" />
                <b>TriSphere Pay</b> will EFT settle T+1 into this account. You can add multiple accounts later.
              </div>
            </div>
          )}

          {S.key === "modules" && (
            <div className="grid gap-3 md:grid-cols-3">
              {([
                ["pay", Wallet, "TriSphere Pay", "Fees, wallets, ledger"],
                ["transport", Bus, "Transport", "Live tracking + billing"],
                ["canteen", Sparkles, "Canteen", "Cashless meals"],
                ["bookshop", Sparkles, "Bookshop", "Uniforms & books"],
                ["health", ShieldCheck, "Health", "Nurse visits + parent alerts"],
                ["academics", GraduationCap, "Academics", "Reports & grades"],
              ] as const).map(([k, Icon, name, desc]) => {
                const on = form.modules[k];
                return (
                  <button key={k}
                    onClick={() => set("modules", { ...form.modules, [k]: !on })}
                    className={`rounded-xl border p-3 text-left transition ${on ? "border-primary bg-primary/5" : "hover:bg-muted/40"}`}>
                    <Icon className={`h-4 w-4 ${on ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="mt-1 font-medium">{name}</div>
                    <div className="text-[11px] text-muted-foreground">{desc}</div>
                    <Badge variant={on ? "default" : "outline"} className="mt-2 text-[10px]">{on ? "Enabled" : "Off"}</Badge>
                  </button>
                );
              })}
            </div>
          )}

          {S.key === "seed" && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border p-4">
                <div className="flex items-center gap-2 font-medium"><Sparkles className="h-4 w-4 text-primary" /> Explore with live demo data</div>
                <p className="mt-1 text-sm text-muted-foreground">
                  We'll insert 24 demo learners, transport routes, fees, health visits and 14 days of settlements
                  — all in your school. You can delete them anytime.
                </p>
                <label className="mt-3 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.seedDemo} onChange={(e) => set("seedDemo", e.target.checked)} />
                  Seed demo data now (recommended)
                </label>
              </div>
              <div className="rounded-xl border bg-gradient-to-br from-primary/10 to-card p-4">
                <div className="text-sm font-medium">Review</div>
                <dl className="mt-2 space-y-1 text-xs">
                  <Row k="Name" v={form.name || "—"} />
                  <Row k="Location" v={`${form.district || "—"} · ${form.region}`} />
                  <Row k="Plan" v={form.subscription_plan} />
                  <Row k="Bank" v={form.settlement_bank || "Add later"} />
                  <Row k="Modules" v={Object.entries(form.modules).filter(([,v]) => v).map(([k]) => k).join(", ")} />
                </dl>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between border-t pt-4">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            <ChevronLeft className="mr-1 h-4 w-4" />Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button disabled={!canAdvance} onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>
              Continue<ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button disabled={create.isPending || !form.name.trim()} onClick={() => create.mutate()}>
              <Rocket className="mr-1 h-4 w-4" />{create.isPending ? "Launching…" : "Launch school"}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
function Row({ k, v }: { k: string; v: string }) {
  return (<div className="flex items-center justify-between gap-2 border-b border-dashed py-0.5"><dt className="text-muted-foreground">{k}</dt><dd className="truncate font-medium">{v}</dd></div>);
}
